import { inject, injectable } from 'tsyringe';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import { PersistentStorageService } from '../../persistent-storage-service/persistent-storage.service';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { ConditionContext } from './interfaces/condition-context.interface';
import { DisplayedNotificationDetails } from '../../persistent-storage-service/models/displayed-notifications';

import { Game } from '../../../models/game.model';
import { Application } from '../../../models/application.model';
import {
  INotificationWindowManagerServiceDelegate,
  kNameOfNotificationWindowManagerService,
  INotificationWindowManagerService,
} from '../../../interfaces/notification-window-manager-service.interface';
import { INotificationDisplayDetails } from 'app-core/src/models/notification-display-details.model';
import { Utils } from '../../../utils/utils';
import { LoggerService } from '../../logger.service';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.AllApps)
export class ConditionNotificationDisplayCap
  implements ICondition, INotificationWindowManagerServiceDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionNotificationDisplayCap',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly storage: PersistentStorageService,
    @inject(kNameOfNotificationWindowManagerService)
    private readonly notifWindowManager: INotificationWindowManagerService,
  ) {
    this.notifWindowManager.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { game, application: app } = context;

    if (app.displayLimit <= 0) {
      this.logger.warn(`The app's displayLimit is set to: ${app.displayLimit}`);
      return [false, null];
    }

    const appInfo = this.storage.getDisplayedNotificationInfo(game, app);

    const reachedDisplayLimit = this.reachedDisplayLimit(app, appInfo);
    if (!reachedDisplayLimit) {
      return [true];
    }

    if (reachedDisplayLimit) {
      const shouldResetCap = await this.shouldResetCap(appInfo);

      if (shouldResetCap) {
        this.resetDisplayCount(appInfo, game, app);
        return [true];
      }
    }

    this.logger.info(
      `App [${app.name}] has Reached the display limit of: ${app.displayLimit}`,
    );
    return [false, NoRecommendationsReasons.CapLimitReached];
  }

  // ---------------------------------------------------------------------------
  private reachedDisplayLimit(
    app: Application,
    appInfo: DisplayedNotificationDetails,
  ) {
    return appInfo?.count >= app.displayLimit;
  }

  // ---------------------------------------------------------------------------
  private async shouldResetCap(displayInfo: DisplayedNotificationDetails) {
    if (!displayInfo?.lastTimestamp) {
      return false;
    }

    const notificationCapResetPeriodInWeeks =
      await this.getNotificationsCapResetPeriodInWeeks();

    const timeSinceLastDisplayedInWeeks = Utils.calcTimePassedInWeeks(
      displayInfo.lastTimestamp,
    );

    const reachedCap =
      notificationCapResetPeriodInWeeks <= timeSinceLastDisplayedInWeeks;

    return reachedCap;
  }

  // ---------------------------------------------------------------------------
  private resetDisplayCount(
    appInfo: DisplayedNotificationDetails,
    game: Game,
    app: Application,
  ) {
    const date = new Date(appInfo?.lastTimestamp);
    this.logger.info(`Reached display cap reset period ${date} - resetting`);
    this.storage.resetDisplayedNotificationCount(game, app);
  }

  // ---------------------------------------------------------------------------
  private async getNotificationsCapResetPeriodInWeeks(): Promise<number> {
    const config = await this.datastoreService.getGlobalConfigurations();
    return config.resetNotificationsCapPeriodInWeeks;
  }

  // ---------------------------------------------------------------------------
  // INotificationWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onNotificationReachedMinTimeToDisplay(
    info: INotificationDisplayDetails,
  ) {
    const { gameId, applicationUid } = info;
    this.logger.info(
      `Updating display count for (${gameId}, ${applicationUid})`,
    );

    this.storage.setNotificationDisplayed(gameId, applicationUid);
  }
}
